import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Stack,
  Select,
  Group,
  Button,
  Text,
  Avatar,
  Badge,
  Box,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle, AlertTriangle, Clock } from 'react-feather';
import {
  cohortCoachesApi,
  type CohortCoachWithDetails,
} from '../../../services/api/cohortCoaches';
import { groupsApi, type GroupWithMembers } from '../../../services/api/groups';
import type { SessionWithDetails } from '../../../services/api/sessions';
import { getInitialsFromParts } from '../../../utils/avatarHelpers';
import { COACH_AVAILABILITY_CONFIG } from '../../../config/statusConfig';
import { collectAssignmentWarnings } from './groups/coachConflictUtils';
import { getApiErrorMessage } from '../../../services/api/client';
import { isMedleyEmail } from '../../../utils/medleyEmail';
import { NeedsMedleyEmailHint } from '../../../components/coaches/NeedsMedleyEmailHint';

interface AssignCoachModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  group: GroupWithMembers | null;
  // MED-978: pass the group's sessions so the conflict warning filters to
  // this group's actual scheduled dates rather than flagging every slot conflict.
  groupSessions?: SessionWithDetails[];
  onAssigned: () => void;
}

// MED-1355: the fade for a coach who cannot be picked (Add Potential Coaches uses 0.5).
const BLOCKED_OPACITY = 0.5;

// Get availability label and color from shared config
const getAvailabilityInfo = (status: CohortCoachWithDetails['availability_status']) => {
  const config = COACH_AVAILABILITY_CONFIG[status];
  return config ? { label: config.label, color: config.color } : { label: '', color: 'gray' };
};

export function AssignCoachModal({
  opened,
  onClose,
  cohortId,
  group,
  groupSessions,
  onAssigned,
}: AssignCoachModalProps) {
  const [coaches, setCoaches] = useState<CohortCoachWithDetails[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  // MED-1355: the server reason for a refused save. It stays in the modal
  // until the selection changes, so the CM can read it and pick again.
  const [serverError, setServerError] = useState<string | null>(null);

  // Fetch eligible coaches
  const fetchCoaches = useCallback(async () => {
    if (!opened) return;

    setLoading(true);
    try {
      const response = await cohortCoachesApi.list(cohortId, { limit: 50 });
      // Filter to only show available or partially available coaches
      const eligible = response.data.filter(
        (c) => c.availability_status === 'available' || c.availability_status === 'partially_available'
      );
      setCoaches(eligible);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  }, [cohortId, opened]);

  useEffect(() => {
    fetchCoaches();
  }, [fetchCoaches]);

  // Reset selection when modal opens
  useEffect(() => {
    if (opened) {
      setSelectedCoachId(null);
      setServerError(null);
    }
  }, [opened]);

  const handleSelectCoach = (value: string | null) => {
    setSelectedCoachId(value);
    setServerError(null);
  };

  // Get coach details for selected coach
  const selectedCoach = coaches.find((c) => c.user_id === selectedCoachId);

  // Compute assignment warnings for the selected coach + group.
  // MED-978: thread the group's sessions so scheduling conflicts filter to
  // this group's actual session dates.
  const warnings = useMemo(() => {
    if (!selectedCoach || !group) return [];
    const sessionsMap = groupSessions ? { [group.id]: groupSessions } : {};
    return collectAssignmentWarnings(selectedCoach, [group], sessionsMap);
  }, [selectedCoach, group, groupSessions]);

  const schedulingWarning = warnings.find((w) => w.type === 'scheduling');
  const interestWarning = warnings.find((w) => w.type === 'interest_limit');
  const hasWarnings = warnings.length > 0;

  // Handle assignment
  const handleAssign = async () => {
    if (!selectedCoachId || !group) return;

    setAssigning(true);
    setServerError(null);
    try {
      await groupsApi.update(cohortId, group.id, { coachId: selectedCoachId });

      const coach = coaches.find((c) => c.user_id === selectedCoachId);
      notifications.show({
        title: 'Coach Assigned',
        message: `${coach?.first_name} ${coach?.last_name} has been assigned to ${group.name}`,
        color: 'green',
      });

      onAssigned();
      onClose();
    } catch (error) {
      // MED-1355: show the reason in the modal and keep the modal open.
      setServerError(getApiErrorMessage(error, 'Failed to assign coach'));
    } finally {
      setAssigning(false);
    }
  };

  // Create select options
  // MED-1355: a group lead needs a Medley email, so a coach without one is
  // disabled, with a link to add it.
  const coachOptions = coaches.map((coach) => {
    const availInfo = getAvailabilityInfo(coach.availability_status);
    const fullName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || coach.email;

    return {
      value: coach.user_id,
      label: `${fullName} (${availInfo.label})`,
      disabled: !isMedleyEmail(coach.email),
    };
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Assign Coach to ${group?.name || 'Group'}`}
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Select a coach to assign to this group. Only coaches with confirmed availability are shown.
        </Text>

        <Select
          label="Select Coach"
          placeholder={loading ? 'Loading coaches...' : 'Choose a coach'}
          data={coachOptions}
          value={selectedCoachId}
          onChange={handleSelectCoach}
          searchable
          clearable
          disabled={loading}
          nothingFoundMessage="No available coaches"
          // A disabled option is faded by Mantine at 0.35 opacity. Keep the
          // option at full opacity and fade only the name, so the link keeps
          // its contrast (the same as Add Potential Coaches).
          styles={{ option: { opacity: 1 } }}
          renderOption={({ option }) => {
            const coach = coaches.find((c) => c.user_id === option.value);
            if (!option.disabled || !coach) return <Text size="sm">{option.label}</Text>;
            const name = `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || 'this coach';
            return (
              <Stack gap={2}>
                <Text size="sm" style={{ opacity: BLOCKED_OPACITY }}>
                  {option.label}
                </Text>
                <NeedsMedleyEmailHint
                  coachProfileId={coach.coach_profile_id}
                  coachName={name}
                  onNavigate={onClose}
                />
              </Stack>
            );
          }}
        />

        {/* Show selected coach info */}
        {selectedCoach && (
          <Group gap="sm" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 6 }}>
            <Avatar size={32} radius="xl" color={getAvailabilityInfo(selectedCoach.availability_status).color}>
              {getInitialsFromParts(selectedCoach.first_name, selectedCoach.last_name)}
            </Avatar>
            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={500}>
                {selectedCoach.first_name} {selectedCoach.last_name}
              </Text>
              <Text size="xs" c="dimmed">{selectedCoach.email}</Text>
            </Box>
            <Badge size="sm" color={getAvailabilityInfo(selectedCoach.availability_status).color} variant="light">
              {getAvailabilityInfo(selectedCoach.availability_status).label}
            </Badge>
          </Group>
        )}

        {/* Scheduling conflict warning */}
        {schedulingWarning && schedulingWarning.type === 'scheduling' && (
          <Alert
            icon={<Clock size={16} />}
            color="yellow"
            variant="light"
            title="Scheduling Conflicts"
          >
            <Text size="sm">
              {schedulingWarning.conflicts.length} conflict{schedulingWarning.conflicts.length !== 1 ? 's' : ''} with {group?.name} sessions
            </Text>
          </Alert>
        )}

        {/* Interest limit warning */}
        {interestWarning && interestWarning.type === 'interest_limit' && (
          <Alert
            icon={<AlertTriangle size={16} />}
            color="yellow"
            variant="light"
            title="Over Interest Limit"
          >
            <Text size="sm">
              This exceeds the coach&apos;s interest of {interestWarning.maxGroups} group{interestWarning.maxGroups !== 1 ? 's' : ''} (currently assigned to {interestWarning.currentCount}).
            </Text>
          </Alert>
        )}

        {/* MED-1355: the server reason for a refused save, above the buttons. */}
        {serverError && (
          <Alert icon={<AlertCircle size={16} />} color="red" variant="light" role="alert">
            <Text size="sm">{serverError}</Text>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            loading={assigning}
            disabled={!selectedCoachId}
            color={hasWarnings ? 'yellow' : undefined}
          >
            {hasWarnings ? 'Assign Anyway' : 'Assign'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AssignCoachModal;
